import {
  AtSign,
  BadgePercent,
  Bot,
  Film,
  Flame,
  Gem,
  GraduationCap,
  Hash,
  Headphones,
  Heart,
  Instagram,
  Lightbulb,
  Linkedin,
  Mail,
  Megaphone,
  MessageCircle,
  Mic,
  MonitorPlay,
  MoreHorizontal,
  Package,
  PenTool,
  Rocket,
  Search,
  Send,
  Share2,
  Sun,
  Terminal,
  Twitter,
  Users,
  Video,
  Youtube,
  type LucideIcon,
} from "@/icons/lucide-compat";

export interface ChipOption {
  /** Value persisted to the server. Matches the shared contract vocabulary. */
  id: string;
  label: string;
  icon: LucideIcon;
}

export const ROLE_OPTIONS: readonly ChipOption[] = [
  { id: "founder", label: "Founder", icon: Rocket },
  { id: "marketer", label: "Marketer", icon: Megaphone },
  { id: "creator", label: "Creator", icon: Video },
  { id: "product", label: "Product", icon: Package },
  { id: "engineer", label: "Engineer", icon: Terminal },
  { id: "designer", label: "Designer", icon: PenTool },
  { id: "student", label: "Student", icon: GraduationCap },
  { id: "other", label: "Other", icon: MoreHorizontal },
];

export const SOURCE_OPTIONS: readonly ChipOption[] = [
  { id: "social", label: "Social Media", icon: Share2 },
  { id: "community", label: "Community", icon: Users },
  { id: "ai-tools", label: "AI tools", icon: Bot },
  { id: "friend", label: "Friend", icon: Heart },
  { id: "google", label: "Google", icon: Search },
  { id: "newsletter", label: "Newsletter", icon: Mail },
  { id: "product-hunt", label: "Product Hunt", icon: Flame },
  { id: "other", label: "Other", icon: MoreHorizontal },
];

export const INTENT_OPTIONS: readonly ChipOption[] = [
  { id: "talking-head", label: "Talking-head", icon: Mic },
  { id: "explainer", label: "Explainer", icon: Lightbulb },
  { id: "product-demo", label: "Product demo", icon: MonitorPlay },
  { id: "launch", label: "Launch video", icon: Rocket },
  { id: "vlog", label: "Vlog", icon: Video },
  { id: "montage", label: "Montage", icon: Film },
  { id: "podcast", label: "Podcast clips", icon: Headphones },
  { id: "ad", label: "Ad / Promo", icon: BadgePercent },
  { id: "other", label: "Something else", icon: MoreHorizontal },
];

/**
 * Some source chips do not answer the question on their own. Picking one
 * replaces the chip row with a sub-question under the same heading.
 */
export type SourceBranch =
  | { kind: "chips"; options: readonly ChipOption[] }
  | { kind: "text"; placeholder: string };

export const SOURCE_BRANCHES: Readonly<Record<string, SourceBranch>> = {
  social: {
    kind: "chips",
    options: [
      { id: "linkedin", label: "LinkedIn", icon: Linkedin },
      { id: "instagram", label: "Instagram", icon: Instagram },
      { id: "twitter", label: "Twitter/X", icon: Twitter },
      { id: "reddit", label: "Reddit", icon: MessageCircle },
      { id: "tiktok", label: "TikTok", icon: Video },
      { id: "youtube", label: "YouTube", icon: Youtube },
    ],
  },
  "ai-tools": {
    kind: "chips",
    options: [
      { id: "gemini", label: "Gemini", icon: Gem },
      { id: "chatgpt", label: "ChatGPT", icon: Bot },
      { id: "claude", label: "Claude", icon: Sun },
      { id: "perplexity", label: "Perplexity", icon: Search },
      { id: "other", label: "Other", icon: MoreHorizontal },
    ],
  },
  community: {
    kind: "chips",
    options: [
      { id: "whatsapp", label: "WhatsApp", icon: MessageCircle },
      { id: "discord", label: "Discord", icon: Users },
      { id: "reddit", label: "Reddit", icon: Hash },
      { id: "telegram", label: "Telegram", icon: Send },
    ],
  },
  other: { kind: "text", placeholder: "Tell us where" },
};

/**
 * The follow-up's own heading.
 *
 * The source step renders twice: once to pick a broad source, once to pin it
 * down. Both used to read "How did you hear about us?", so the flow looked
 * like it had lost the first answer and was asking again. Naming the specific
 * thing being asked makes the second screen read as what it is, a follow-up,
 * and quietly confirms the first answer was heard.
 */
export const SOURCE_FOLLOWUP_HEADINGS: Readonly<Record<string, string>> = {
  social: "Which platform?",
  "ai-tools": "Which assistant?",
  community: "Which community?",
  other: "Where did you hear about us?",
};

export function sourceFollowupHeading(sourceId: string | null): string | null {
  if (sourceId === null) return null;
  return SOURCE_FOLLOWUP_HEADINGS[sourceId] ?? null;
}

export function getSourceBranch(sourceId: string | null): SourceBranch | null {
  if (sourceId === null) return null;
  return SOURCE_BRANCHES[sourceId] ?? null;
}

/** True when picking this source opens a sub-question instead of answering. */
export function isBranchSource(sourceId: string | null): boolean {
  return getSourceBranch(sourceId) !== null;
}

/** Icon used by the free-text source chip. */
export const SOURCE_TEXT_ICON = AtSign;
